/**
 * <park-maintenance-banner> — public maintenance notice (epic #138, issue #148;
 * #114 feature). Isomorphic: the pure renderParkMaintenanceBanner() is shared by
 * the SSR wrapper components/ds/ParkMaintenanceBanner.astro (which owns the
 * status fetch + i18n + date formatting and is rendered in ParkBase) and the
 * custom element. See docs/design-system-rebuild-plan.md.
 *
 * Reproduces the live `.park-maint(--active|--soon)` markup 1:1. Title/detail are
 * PRE-RESOLVED strings (the wrapper builds them from the schedule + locale), so
 * the element carries no i18n/date logic. Component CSS: wc/css/park-maintenance-banner.css.
 *
 * Attributes:
 *   - variant="active|soon"   active → closed (role=alert); soon → upcoming (role=status).
 *   - title="…"               bold lead (incl. emoji).
 *   - detail="…"              the reason + reopen/start time.
 *
 * @dsSpec frontend#FR-11 — the maintenance notice shown on every page, no-JS visible.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-maintenance-banner";

export type MaintVariant = "active" | "soon";

export interface ParkMaintenanceProps {
	variant?: MaintVariant;
	title?: string;
	detail?: string;
}

/** Pure render: props → inner HTML string. No DOM access (server + client safe). */
export function renderParkMaintenanceBanner(
	props: ParkMaintenanceProps,
): string {
	const active = props.variant === "active";
	const cls = active
		? "park-maint park-maint--active"
		: "park-maint park-maint--soon";
	const role = active ? "alert" : "status";
	return html`<aside class="${cls}" role="${role}"><strong class="park-maint__title">${esc(props.title)}</strong><span class="park-maint__detail">${esc(props.detail)}</span></aside>`;
}

class ParkMaintenanceBanner extends ParkElement {
	static get observedAttributes(): string[] {
		return ["variant", "title", "detail"];
	}

	protected render(): string {
		return renderParkMaintenanceBanner({
			variant: this.attr("variant") === "active" ? "active" : "soon",
			title: this.attr("title"),
			detail: this.attr("detail"),
		});
	}
}

defineElement(TAG, ParkMaintenanceBanner);

export { ParkMaintenanceBanner };
